import { Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import { OpenedPackCalculatorPopover } from "./opened-pack-calculator";
import { type OpnameItemErrors } from "./stock-opname-schema";
import { mergeCalculatorNote } from "./stock-opname-utils";

import type { StockOpnameItem, StockReason } from "@artisancode/api-types";

/** Callbacks + flags shared by the mobile card and the desktop row. */
export interface OpnameItemProps {
  item: StockOpnameItem;
  reasons: StockReason[];
  isRawMaterial: boolean;
  isLocked: boolean;
  onOnHandChange: (itemId: string, value: string) => void;
  onRepackingChange?: (itemId: string, value: string) => void;
  onReasonChange: (itemId: string, reasonId: string) => void;
  onNoteChange: (itemId: string, note: string) => void;
  /** Removes the line from a draft session. */
  onRemove?: (lineId: string) => void;
  /** Problems found by the completion check, shown on the matching field. */
  error?: OpnameItemErrors[string];
}

export function OpnameDiffBadge({
  diff,
  className,
}: {
  diff: number;
  className?: string;
}) {
  if (diff === 0) {
    return (
      <span className="font-mono text-xs font-medium text-muted-foreground">
        0
      </span>
    );
  }
  return (
    <Badge
      variant={diff < 0 ? "destructive" : "default"}
      className={cn(
        "px-1.5 py-0 font-mono text-[10px]",
        diff > 0 && "bg-emerald-600 text-white hover:bg-emerald-600",
        className,
      )}
    >
      {diff > 0 ? `+${diff}` : diff}
    </Badge>
  );
}

/** Counted-qty input, or the read-only value once the session is locked. */
export function CountedQtyField({
  value,
  isLocked,
  invalid,
  onChange,
  className,
}: {
  value: number | null;
  isLocked: boolean;
  invalid?: boolean;
  onChange: (value: string) => void;
  className?: string;
}) {
  if (isLocked) {
    return (
      <span className="font-mono font-bold">
        {value !== null ? value.toLocaleString() : "-"}
      </span>
    );
  }
  return (
    <NumberInput
      decimals={4}
      showZero
      value={value ?? undefined}
      onChange={(n, raw) => onChange(raw === "" ? "" : String(n))}
      // Empty means "not counted yet"; 0 must be typed explicitly.
      placeholder="—"
      aria-invalid={invalid || undefined}
      className={cn(
        "text-right font-mono text-xs",
        invalid && "border-destructive ring-1 ring-destructive",
        className,
      )}
    />
  );
}

/** Bar (repacking) count + the opened-pack calculator that fills it. */
export function RepackingCountField({
  item,
  isLocked,
  error,
  onRepackingChange,
  onNoteChange,
  inputClassName,
}: Pick<
  OpnameItemProps,
  "item" | "isLocked" | "error" | "onRepackingChange" | "onNoteChange"
> & { inputClassName?: string }) {
  const field = (
    <CountedQtyField
      value={item.countedRepacking}
      isLocked={isLocked}
      invalid={!!error?.repacking}
      onChange={(v) => onRepackingChange?.(item.id, v)}
      className={inputClassName}
    />
  );
  if (isLocked) return field;

  return (
    <>
      {field}
      <OpenedPackCalculatorPopover
        itemName={item.itemName}
        uomName={item.uomName}
        initialActualQty={item.countedRepacking ?? 0}
        initialPackSize={item.packQty}
        onApply={(totalQty, note) => {
          onRepackingChange?.(item.id, totalQty.toString());
          if (note) onNoteChange(item.id, mergeCalculatorNote(note, item.note));
        }}
      />
    </>
  );
}

export function ReasonSelect({
  reasons,
  value,
  invalid,
  onChange,
  className,
}: {
  reasons: StockReason[];
  value: string;
  invalid: boolean;
  onChange: (reasonId: string) => void;
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        aria-invalid={invalid || undefined}
        className={cn(
          "text-xs",
          invalid &&
            "border-destructive text-destructive ring-1 ring-destructive",
          className,
        )}
      >
        <SelectValue placeholder="Pilih alasan selisih..." />
      </SelectTrigger>
      <SelectContent>
        {reasons.map((r) => (
          <SelectItem key={r.id} value={r.id} className="text-xs">
            {r.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function RemoveItemButton({
  itemName,
  onClick,
}: {
  itemName: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={onClick}
      aria-label={`Hapus ${itemName} dari opname`}
      title="Hapus dari opname"
      className="h-8 w-8 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );
}
